import { useMemo } from 'react';
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { useTranslation } from '@weareconceptstudio/core';
export const validatePhoneNumber = ({ value, required, errorKey, translate }) => {
    const trimmed = (value || '').trim();
    // If empty
    if (trimmed === '') {
        if (required) {
            return translate('form.validateMessages.required', {
                errorKey: translate(errorKey),
            });
        }
        return true;
    }
    const phoneNumber = parsePhoneNumberFromString(trimmed);
    // If parse fails, try to detect if it's just a prefix
    if (!phoneNumber) {
        // Check if it's just a prefix (like +374)
        const isJustPrefix = getCountries().some((countryCode) => {
            const code = '+' + getCountryCallingCode(countryCode);
            return trimmed === code;
        });
        if (isJustPrefix) {
            if (required) {
                return translate('form.validateMessages.required', {
                    errorKey: translate(errorKey),
                });
            }
            return true; // It's okay if not required
        }
        // Not a valid prefix or number
        return translate('form.validateMessages.phone', {
            errorKey: translate(errorKey),
        });
    }
    // If it's only prefix and nationalNumber is empty
    if (phoneNumber.nationalNumber === '') {
        if (required) {
            return translate('form.validateMessages.required', {
                errorKey: translate(errorKey),
            });
        }
        return true;
    }
    // Validate number
    if (!phoneNumber.isValid()) {
        return translate('form.validateMessages.phone', {
            errorKey: translate(errorKey),
        });
    }
    return true;
};
export const validationPatterns = ({ type, pattern, message, translate }) => {
    switch (type) {
        case 'email':
            return {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/g,
                message: message || translate('form.validateMessages.email'),
            };
        case 'new-password':
            return { value: /.{8,}/, message: message || translate('form.validateMessages.password') };
        case 'number':
            return { value: /[0-9]/g, message };
        default:
            return { value: pattern, message };
    }
};
export const useRules = ({ rules, required, requiredMessage, childType, errorKey }) => {
    const { translate } = useTranslation();
    return useMemo(() => {
        let r = [...rules];
        if (required) {
            r.unshift({ required: true, message: requiredMessage });
            if (childType.toLowerCase() === 'input' || childType.toLowerCase() === 'textarea') {
                r.push({
                    validator: (value) => {
                        if (!value || typeof value !== 'string' || value.trim().length === 0) {
                            return translate('form.validateMessages.whitespace', { errorKey: translate(errorKey) });
                        }
                        return true;
                    },
                });
            }
        }
        if (childType === 'phone') {
            r.push({
                validator: (value) => validatePhoneNumber({ value, required, translate, errorKey }),
            });
        }
        return r;
    }, [rules, required, requiredMessage, childType, errorKey]);
};
export const mapValidationRules = (errorKey, rules) => {
    const { translate } = useTranslation();
    return rules?.reduce((acc, rule) => {
        if (rule.required) {
            acc.required = {
                value: rule.required,
                message: rule.message || translate('form.validateMessages.required', { errorKey: translate(errorKey) }),
            };
        }
        if (rule.pattern || rule.type) {
            acc.pattern = validationPatterns({ ...rule, translate });
        }
        if (rule.minLength) {
            acc.minLength = { value: rule.minLength, message: rule.message || `Minimum length is ${rule.minLength}` };
        }
        if (rule.maxLength) {
            acc.maxLength = { value: rule.maxLength, message: rule.message || `Maximum length is ${rule.maxLength}` };
        }
        if (rule.validator) {
            acc.validate = async (value) => {
                return (await rule.validator(value)) || rule.message;
            };
        }
        return acc;
    }, {});
};
//# sourceMappingURL=rules.js.map